import { SimplifiedPortCall, ScheduleVesselAvailability, ScheduleEntry, VesselStatusSchedule } from '../models/types/voyage-schedules-model.types';
import { authFetchDELETE, authFetchGET, authFetchPOST, authFetchPUT, FetchStatusCodeError } from '../utils/auth-fetch';
import { IndexedDBService } from "../../indexed-db/services/db_service";
import { isOnline } from '../utils/helpers';
import { VoyageImoModel } from '../models/voyage-imo-model';

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
const schedulesApi = (<any>window)["appenv"].services.schedulesapi;
const env = { ...schedulesApi, ...isVesselOnCloud ? { scope: (<any>window)["appenv"].scope } : {} };

export interface ScheduledVoyageLeg {
    id: string;
    departureTerminalCode: string;
    departureTerminalUnLocode: string;
    departureAt: string;
    arrivalTerminalCode: string;
    arrivalTerminalUnLocode: string;
    departureTerminalServiceCode?: string;
    arrivalTerminalServiceCode?: string;
    arrivalAt: string;
    arrivalPortCall?: {
        GSIS: SimplifiedPortCall | null;
    };
    departurePortCall?: {
        GSIS: SimplifiedPortCall | null;
    };
}

export interface PortCall {
    id: number;
    submittedAt: string;
    imo: number;
    terminalCode: string;
    terminalUnLocode: string;
    arrivalAt: string;
    departureAt: string;
    arrivalDirection: string;
    serviceCode?: string; 
    operatorCode?: string
}

export interface VesselStatus {
    status: string;
    voyage: ScheduledVoyageLeg;
    portCall: PortCall;
}

export interface PortCallChange {
    id?: number;
    gsisId: number | null;
    createDtm?: string;
    terminalCode: string;
    arrivalAt: string;
    departureAt: string;
    estimatedpilotboarding?: string;
    estimatedpilotdropoff?: string;
    changeType?: "add" | "change" | "delete";
}


export interface BPRIdDetails {
    schedule_id: string;
    departure_terminal_gsis_id?: number | null;
    arrival_terminal_gsis_id?: number | null;
    departure_terminal_code: string;
    arrival_terminal_code: string;
    metadata: Metadata;
    isBprAvailable?: boolean;
  }
  
  interface Metadata {
    bprIdList: string[];
  }


export const SchedulesService = new class extends EventTarget {

    async fetchScheduleVesselAvailability(gapThreshold?: number): Promise<{ [index: string]: ScheduleVesselAvailability }> {
        let options: { [index: string]: string } = {}

        if (gapThreshold) {
            options.gapThreshold = `${gapThreshold}`
        }

        return await authFetchGET(env.scope, `${env.baseurl}/vessel-availability`, options);
    }

    async fetchScheduleDetailsForVessel(imoNo: number, includeNumBefore = 0, fromDate = new Date()): Promise<{ [index: number]: ScheduleEntry[] }> {
        let schedulesPromise = this._fetchSchedulesForVessel(imoNo, includeNumBefore, fromDate.toISOString());
        let vesselStatusPromise = this.fetchScheduleVesselStates(imoNo);

        let schedules = await schedulesPromise;
        const vesselStatus = await vesselStatusPromise;

        if (vesselStatus.length === 0 || (schedules && !schedules[imoNo])) {
            return schedules;
        }

        schedules[imoNo].forEach(item => {
            item.active = item.etd === vesselStatus[0].schedule.startTime || item.etd === vesselStatus[0].schedule.endTime;
            item.inPort = item.active && vesselStatus[0].schedule.inPort;
        });

        return schedules;
    }

    async fetchScheduleVesselStates(imoNo: number | null = null, buttonState?: string) {
        let results = [];
            try {
                let options: { [index: string]: string } = isVesselOnCloud ? {} : {
                    onlyMaerskOperated: "false"
                };
                if (imoNo !== null) {
                    options.imo = `${imoNo}`;
                }

                if ((isVesselOnCloud && !isOnline())) {
                    // if offline get vesselStatus data from IndexedDB
                    const cacheData = await IndexedDBService.vesselStatus.get();
                    results = [{
                        imoNo,
                        schedule: { ...cacheData[0] }
                    }];
                    return results;
                }
                
                const fixedBaseUrl = isVesselOnCloud ? env.baseurl.replace(/\/v\d+\/?$/, '') : env.baseurl; // Remove trailing version segment for cloud

                const url = isVesselOnCloud ? `${fixedBaseUrl}/v2/vesselstatus` : `${env.baseurl}/vesselstatus`;

                const data =  await authFetchGET(env.scope, url, options);
                if (data?.error && (isVesselOnCloud && !isOnline())) {
                    throw new Error(`API Error: ${data.error}`);
                }

                if (buttonState === "NowButton") {
                results.push(data)
                } else {
                    for (let imo in data) {
                        const item = this._mapScheduleVesselStates((data[imo].length ? data[imo][0] : data[imo]), imo);
                        if(isVesselOnCloud) {
                            await IndexedDBService.vesselStatusActual.delete('id');
                            await IndexedDBService.vesselStatusActual.add(data[imo].length ? data[imo][0] : data[imo]);
                        }
                        if (item) {
                            results.push(item);
                        }
                    }
                }
                if (isVesselOnCloud && results && results[0]) {
                    await IndexedDBService.vesselStatus.delete('id');
                    await IndexedDBService.vesselStatus.add(results[0]?.schedule);
                }
            }
            catch (err) {
                if (err instanceof FetchStatusCodeError) {
                // Return empty array when no schedule is available (ie. 404 error)
                    if (err.response.status === 404) {
                        return [];
                    }
                }
                throw err;
            }

        return results;
    }

    async changePortCall(change: PortCallChange): Promise<PortCallChange> {
        let url: string;
        if (change.gsisId) {
            url = `${env.baseurl}/portcalls/gsis/${change.gsisId}`;
        }
        else {
            if (!change.id) {
                throw new Error("Neither GSIS nor local id specified");
            }
            url = `${env.baseurl}/portcalls/local/${change.id}`;
        }

        return await authFetchPUT(env.scope, url, change);
    }

    async deletePortCall(gsisId: number | null, localId: number | null): Promise<PortCallChange | undefined> {
        let url: string;
        if (gsisId) {
            url = `${env.baseurl}/portcalls/gsis/${gsisId}`;
        }
        else {
            if (!localId) {
                throw new Error("Neither GSIS nor local id specified");
            }
            url = `${env.baseurl}/portcalls/local/${localId}`;
        }

        return await authFetchDELETE(env.scope, url);
    }

    async addPortCall(newPortCall: PortCallChange): Promise<PortCallChange> {
        return await authFetchPOST(env.scope, `${env.baseurl}/portcalls/local`, newPortCall);
    }

    private async _fetchSchedulesForVessel(imoNo: number, includeNumBefore: number, fromDate: string) {
        try {
            const apiURL= isVesselOnCloud ? 'vesselvoyagelegs' : 'voyagelegs';
            if ((isVesselOnCloud && !isOnline())) {
                // if offline get schedules Data from IndexedDB
                const cacheData = await IndexedDBService.schedules.getAll();
                const data = {
                    [imoNo]: cacheData
                };
                return data;
            }
            const data: { [index: number]: ScheduleEntry[] } = await authFetchGET(env.scope, `${env.baseurl}/${apiURL}`,
                { imo: `${imoNo}`, b: `${includeNumBefore}`, t: fromDate });

            if ('error' in data && (isVesselOnCloud && !isOnline())){
                throw new Error(`API Error: ${(data as any).error}`);
            }
        
            if (data?.[imoNo]) {
                data[imoNo].forEach((item: ScheduleEntry) => {
                    item.etd = (new Date(item.departureAt)).getTime();
                    item.eta = (new Date(item.arrivalAt)).getTime();
                    item.progress = Math.ceil(Math.max(0, ((new Date().getTime() - item.etd) * 100) / (item.eta - item.etd)));
                });
            }
            return data;
        } catch (err) {
            console.log(err);
            return {};
        }
    }

    private _mapScheduleVesselStates(vesselStatus: VesselStatus, imo: string){ 
        switch(vesselStatus.status) {
            case 'on-voyage':
                return {
                    imoNo: Number.parseInt(imo),
                    schedule: {
                        startPort: vesselStatus.voyage.departureTerminalCode,
                        endPort: vesselStatus.voyage.arrivalTerminalCode,
                        startTime: (new Date(vesselStatus.voyage.departureAt)).getTime(),
                        endTime: (new Date(vesselStatus.voyage.arrivalAt)).getTime(),
                        inPort: false,
                        serviceCode: vesselStatus.voyage.departurePortCall?.GSIS?.serviceCode,
                        operatorCode: vesselStatus.voyage.departurePortCall?.GSIS?.operatorCode ?? vesselStatus.voyage.arrivalPortCall?.GSIS?.operatorCode
                    },
                };
            case 'in-port':
                return {
                    imoNo: Number.parseInt(imo),
                    schedule: {
                        startPort: vesselStatus.portCall.terminalCode,
                        endPort: vesselStatus.portCall.terminalCode,
                        startTime: (new Date(vesselStatus.portCall.arrivalAt)).getTime(),
                        endTime: (new Date(vesselStatus.portCall.departureAt)).getTime(),
                        inPort: true,
                        serviceCode: vesselStatus.portCall.serviceCode,
                        operatorCode: vesselStatus.portCall.operatorCode
                    },
                };
            default:
                return null;
        }
    }

    async getBPRAvailability(departureTerminalCode: string, arrivalTerminalCode: string, scheduleId: string) {
        // Construct the URL with query parameters
        const url: string = `${env.baseurl}/bprlist?departureTerminalCode=${departureTerminalCode}&arrivalTerminalCode=${arrivalTerminalCode}&scheduleId=${scheduleId}`;
        try {
            // Await the fetch call to resolve, and then await the call to .json() on the response
            const response = await authFetchGET(env.scope, url);

            // Check if the response is an empty array
            if (Array.isArray(response) && response.length === 0) {
                return null
                // Handle the empty array case here
            } else {
                return response
            }   
        } catch (error) {
            console.warn('There has been a problem with your fetch operation:', error);
        }
    }

    async getBPRIDListPerSchedule(departureTerminalCode: string, arrivalTerminalCode: string, scheduleId: string): Promise<string[]> {
        try {
            if (isVesselOnCloud) {
                // Cloud endpoint: /v1/bprs-by-schedule
                const imo = VoyageImoModel?.imo;
                const url: string = `${env.baseurl}/bprs-by-schedule?scheduleId=${scheduleId}&imo=${imo}`;
                const response = await authFetchGET(env.scope, url);
                
                // Response is a list of string IDs
                if (Array.isArray(response)) {
                    return response;
                }
                return [];
            } else {
                // Edge endpoint: /v1/bprlist
                const url: string = `${env.baseurl}/bprlist?departureTerminalCode=${departureTerminalCode}&arrivalTerminalCode=${arrivalTerminalCode}&scheduleId=${scheduleId}`;
                const response = await authFetchGET(env.scope, url);
                
                // Response is a list of BPRResponse objects, extract bprIdList from metadata
                if (Array.isArray(response) && response.length > 0) {
                    const bprIdList = response[0]?.metadata?.bprIdList || [];
                    return bprIdList;
                }
                return [];
            }
        } catch (error) {
            console.warn('Error fetching BPR ID list per schedule:', error);
            throw error;
        }
    }

    async fetchVesselsList() {
        return await authFetchGET(env.scope, `${env.baseurl}/vessels?primaryVoyagePlanningTool=3`);
    }
}
